import { getAuthHeader } from '.';
import { ROOT_URL } from '../utils/constants';
import type { Alpaca } from '../models/Alpaca';
import type { AlpacaImage } from '../models/AlpacaImage';
import type { GalleryImage } from '../models/GalleryImage';

const CLOUD_NAME = import.meta.env.VITE_CLOUD_NAME;
const cloudinaryUrl = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/upload`;

// "Unexpected end of JSON input" Error .message when no content returned

class AlpacaApi {
  static async getAlpacas(search: string): Promise<Alpaca[]> {
    const [headers, userDetails] = await getAuthHeader();

    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas?view=${search}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getAlpacasDataOnly(): Promise<Alpaca[]> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas?data=only`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async search(searchTerms: string): Promise<Alpaca[]> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas/search?searchTerms=${searchTerms}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getAlpaca(alpacaId: number): Promise<Alpaca> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas/${alpacaId}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getAlpacaDataOnly(alpacaId: number): Promise<Alpaca> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas/${alpacaId}?data=only`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getAlpacasByFarmId(farmId: number): Promise<Alpaca[]> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/farms/${userDetails?.userId}/${farmId}/alpacas`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getAlpacasOnlyByFarmId(farmId: number): Promise<Alpaca[]> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/farms/${userDetails?.userId}/${farmId}/alpacas?data=only`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  static async createAlpaca(alpaca: Alpaca): Promise<Alpaca> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas`,
      {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify(alpaca),
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async updateAlpaca(alpaca: Alpaca): Promise<Alpaca> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas/${alpaca.alpacaId}`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify(alpaca),
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async deleteAlpaca(alpacaId: number) {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas/${alpacaId}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return true;
  }
  static async saveImage({
    alpacaId,
    file,
  }: {
    alpacaId: number;
    file: string;
  }): Promise<AlpacaImage> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails.userId}/alpacas/${alpacaId}/images`,
      {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: JSON.stringify({
          imageName: file,
          alpacaId: alpacaId,
          applicationUserId: userDetails.userId,
        }),
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async uploadImage(file: FormData) {
    const response = await fetch(cloudinaryUrl, {
      method: 'POST',
      body: file,
    });
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async getImagesForAlpaca(alpacaId: number): Promise<AlpacaImage[]> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/alpacas/${alpacaId}/images`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
  static async deleteAlpacaImage({
    alpacaId,
    imageName,
  }: {
    alpacaId: number;
    imageName: string;
  }) {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails.userId}/alpacas/${alpacaId}/images/${imageName}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response;
  }
  // static async uploadCsv( file: FormData) {
  //   const [headers, userDetails] = await getAuthHeader();
  //   if (!userDetails) throw new Error("Please Re-Authenticate");
  //   const response = await fetch(
  //     `${ROOT_URL}/api/account/${userDetails.userId}/alpacas/upload`,
  //     {
  //       method: "POST",
  //       credentials: "include",
  //       headers: headers;,
  //       body: file,
  //     },
  //   );

  //   return response.json();
  // }
  static async getGallery(): Promise<GalleryImage[]> {
    const [headers, userDetails] = await getAuthHeader();
    const response = await fetch(
      `${ROOT_URL}/api/account/${userDetails?.userId}/images`,
      {
        method: 'GET',
        credentials: 'include',
        headers: headers,
      },
    );
    if (!response.ok) {
      throw new Error(`${response.status} ${response.statusText}`);
    }
    return response.json();
  }
}

export default AlpacaApi;
